import { useEffect, useMemo, useState } from "react";
import { Search, X, Mail, Building2, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

const STATUS_COLORS = {
    new: "text-[#0077b6] border-[#00aeef]/40 bg-[#00aeef]/10",
    contacted: "text-amber-700 border-amber-400/40 bg-amber-400/10",
    quoted: "text-neutral-800 border-neutral-300 bg-neutral-100",
    won: "text-emerald-700 border-emerald-400/40 bg-emerald-400/10",
    lost: "text-red-700 border-red-400/40 bg-red-400/10",
};

const LEAD_TIME_LABEL = {
    standard: "Standard · 24h",
    priority: "Priority · 3–5d",
    flexible: "Flexible · 2–4w",
};

export default function AdminQuotes() {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/bulk-quotes")
            .then((r) => {
                if (!cancelled) {
                    setQuotes(r.data || []);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        let list = quotes;
        if (filter !== "all") list = list.filter((q) => q.status === filter);
        const q = query.trim().toLowerCase();
        if (q) {
            list = list.filter(
                (x) =>
                    (x.institution || "").toLowerCase().includes(q) ||
                    (x.name || "").toLowerCase().includes(q) ||
                    (x.email || "").toLowerCase().includes(q) ||
                    (x.country || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [quotes, filter, query]);

    const selected = useMemo(() => quotes.find((q) => q.id === selectedId), [quotes, selectedId]);

    const stats = useMemo(() => {
        const byStatus = { new: 0, contacted: 0, quoted: 0, won: 0, lost: 0 };
        let totalUnits = 0;
        for (const q of quotes) {
            byStatus[q.status] = (byStatus[q.status] || 0) + 1;
            totalUnits += q.total_units || 0;
        }
        const conversion = quotes.length ? ((byStatus.won / quotes.length) * 100).toFixed(1) : "0.0";
        return { byStatus, totalUnits, conversion, count: quotes.length };
    }, [quotes]);

    const changeStatus = async (id, newStatus) => {
        setSavingId(id);
        try {
            const { data } = await api.patch(`/admin/bulk-quotes/${id}`, { status: newStatus });
            setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-quotes">
            <div className="mb-6 sm:mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">// Bulk quote inbox</div>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter">
                    Quote requests
                </h1>
                <p className="text-neutral-500 text-sm mt-2">
                    Institutional and volume-buyer enquiries — reply, mark as won, and track conversion.
                </p>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="quotes-kpis">
                <Kpi label="Total" value={stats.count} testid="kpi-total" />
                <Kpi label="New" value={stats.byStatus.new} testid="kpi-new" accent />
                <Kpi label="Contacted" value={stats.byStatus.contacted} testid="kpi-contacted" />
                <Kpi label="Won" value={stats.byStatus.won} testid="kpi-won" />
                <Kpi label="Conversion" value={`${stats.conversion}%`} testid="kpi-conversion" accent />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                    {["all", ...STATUSES].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-lg border transition-colors ${
                                filter === s
                                    ? "border-[#0089c8] text-[#0077b6] bg-[#00aeef]/10"
                                    : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
                            }`}
                            data-testid={`quotes-filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <div className="relative sm:ml-auto sm:w-72">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search institution, name, email…"
                        className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-9 pr-3 py-2.5 text-sm text-neutral-900"
                        data-testid="quotes-search"
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 border border-red-500/40 bg-red-50 text-red-700 text-xs p-3 rounded-lg" data-testid="quotes-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-neutral-500 text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-neutral-200 rounded-xl p-12 text-center text-neutral-500 text-sm" data-testid="quotes-empty">
                    No quote requests match this filter yet.
                </div>
            ) : (
                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
                    <div className="hidden md:grid grid-cols-[1.6fr_1.4fr_0.7fr_0.7fr_1fr_0.7fr] gap-3 px-5 py-3 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                        <div>Institution</div>
                        <div>Contact</div>
                        <div>Units</div>
                        <div>Lead</div>
                        <div>Status</div>
                        <div className="text-right">Received</div>
                    </div>
                    <div className="divide-y divide-neutral-200">
                        {filtered.map((q) => (
                            <button
                                type="button"
                                key={q.id}
                                onClick={() => setSelectedId(q.id)}
                                className="w-full text-left grid grid-cols-1 md:grid-cols-[1.6fr_1.4fr_0.7fr_0.7fr_1fr_0.7fr] gap-2 md:gap-3 px-5 py-4 hover:bg-neutral-50 transition-colors"
                                data-testid={`quote-row-${q.id}`}
                            >
                                <div>
                                    <div className="font-semibold text-neutral-900 text-sm flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-[#0077b6]" />
                                        {q.institution}
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-0.5">{q.country}</div>
                                </div>
                                <div className="text-sm text-neutral-800">
                                    <div>{q.name}</div>
                                    <div className="text-xs text-neutral-500 mt-0.5 truncate">{q.email}</div>
                                </div>
                                <div className="text-sm font-bold text-[#0077b6]">{q.total_units}</div>
                                <div className="text-xs text-neutral-600">{LEAD_TIME_LABEL[q.lead_time] || q.lead_time}</div>
                                <div>
                                    <span
                                        className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border ${
                                            STATUS_COLORS[q.status] || STATUS_COLORS.new
                                        }`}
                                    >
                                        {q.status}
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 md:text-right">
                                    {new Date(q.created_at).toLocaleDateString()}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Detail drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex" data-testid="quote-detail-drawer">
                    <button
                        type="button"
                        onClick={() => setSelectedId(null)}
                        aria-label="Close"
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                    />
                    <div className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto">
                        <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-5 sm:px-8 py-4 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">// Quote request</div>
                                <div className="font-display text-xl sm:text-2xl font-bold text-neutral-900 truncate">
                                    {selected.institution}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                    {selected.name} · {selected.country} ·{" "}
                                    {new Date(selected.created_at).toLocaleString()}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedId(null)}
                                className="p-2 border border-neutral-200 rounded-lg hover:border-[#0089c8]"
                                data-testid="quote-detail-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-5 sm:px-8 py-6 space-y-6">
                            {/* Status controls */}
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">
                                    Status
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            disabled={savingId === selected.id}
                                            onClick={() => changeStatus(selected.id, s)}
                                            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                                                selected.status === s
                                                    ? STATUS_COLORS[s]
                                                    : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
                                            }`}
                                            data-testid={`quote-status-${s}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Contact + reply */}
                            <div className="border border-neutral-200 rounded-lg p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Contact</div>
                                <div className="text-sm text-neutral-900">
                                    <a
                                        href={`mailto:${selected.email}?subject=${encodeURIComponent(
                                            `Nexus Peptides · Bulk quote for ${selected.institution}`
                                        )}`}
                                        className="inline-flex items-center gap-2 text-[#0077b6] hover:underline"
                                        data-testid="quote-reply-email"
                                    >
                                        <Mail className="w-4 h-4" /> {selected.email}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                                {selected.phone && (
                                    <div className="text-xs text-neutral-500 mt-1">Phone: {selected.phone}</div>
                                )}
                                <div className="text-xs text-neutral-500 mt-1">
                                    Preferred lead time: {LEAD_TIME_LABEL[selected.lead_time] || selected.lead_time}
                                </div>
                            </div>

                            {/* Items */}
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">
                                    Requested compounds · total {selected.total_units} units
                                </div>
                                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                                    <div className="grid grid-cols-[1.5fr_0.7fr_0.5fr] px-4 py-2 bg-neutral-50 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                                        <div>Compound</div>
                                        <div>Dose</div>
                                        <div className="text-right">Qty</div>
                                    </div>
                                    <div className="divide-y divide-neutral-200">
                                        {(selected.items || []).map((it, i) => (
                                            <div
                                                key={`${it.compound}-${it.dose || "nodose"}-${i}`}
                                                className="grid grid-cols-[1.5fr_0.7fr_0.5fr] px-4 py-3 text-sm text-neutral-800"
                                                data-testid={`quote-item-${i}`}
                                            >
                                                <div className="font-medium">{it.compound}</div>
                                                <div className="text-neutral-500">{it.dose || "—"}</div>
                                                <div className="text-right font-bold text-[#0077b6]">{it.quantity}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            {selected.notes && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Notes</div>
                                    <div className="border border-neutral-200 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap">
                                        {selected.notes}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, testid, accent }) {
    return (
        <div
            className={`border rounded-xl px-4 py-3 ${accent ? "border-[#00aeef]/40 bg-[#00aeef]/[0.06]" : "border-neutral-200 bg-white"}`}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">{label}</div>
            <div className={`font-display font-bold text-2xl mt-1 ${accent ? "text-[#0077b6]" : "text-neutral-900"}`}>
                {value}
            </div>
        </div>
    );
}
